import { Link } from '@/lib/react-router-dom-shim';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MapPin } from 'lucide-react';

interface PopularDestination {
  slug: string;
  name: string;
  state: string | null;
}

export const FooterPopularDestinations = ({ limit = 6 }: { limit?: number }) => {
  const { t } = useTranslation();

  const { data: destinations = [], isLoading } = useQuery({
    queryKey: ['footer-popular-destinations', limit],
    queryFn: async (): Promise<PopularDestination[]> => {
      const { data, error } = await supabase
        .from('destinations')
        .select('slug, name, state, visit_count')
        .eq('status', 'published')
        .order('visit_count', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as PopularDestination[];
    },
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  if (isLoading || destinations.length === 0) return null;

  return (
    <div className="pt-6 border-t border-primary-foreground/20 mt-6">
      <h4 className="mb-4 font-semibold text-sm text-primary-foreground flex items-center gap-2">
        <MapPin className="h-4 w-4" />
        {t('footer.popularDestinations') || 'Destinos Populares'}
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-x-6 gap-y-1.5">
        {destinations.map((d) => (
          <Link
            key={d.slug}
            to={`/destino/${d.slug}`}
            className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors truncate"
            title={d.name}
          >
            {d.name}
          </Link>
        ))}
      </div>
    </div>
  );
};
